import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { SpAccount } from '../SpAccountContext';
import { API_BASE, downloadExport } from '../api';

interface Props { account: SpAccount; marketplaceId: number | null; }

interface ReportRow {
  id: string; report_date: string; marketplace_id: number; asin: string | null; sku: string | null;
  sessions: number | null; page_views: number | null; buy_box_percentage: number | null;
  units_ordered: number | null; units_ordered_b2b: number | null;
  ordered_product_sales: number | null; total_order_items: number | null; synced_at: string;
}

const REPORT_TYPES = [
  { value: 'GET_SALES_AND_TRAFFIC_REPORT', label: 'Sales & Traffic' },
];

// ── Weekly matrix (mirrors the "Omzet per week" spreadsheet) ──────────────────

interface MCell { s: number; u: number; }
interface MWeek {
  week: number; week_start: string; week_end: string;
  total: number; prev_total: number; diff: number;
  units_total: number; prev_units_total: number; units_diff: number;
  cells: Record<string, MCell>;
}
interface MMarketplace { id: number; code: string; domain: string; vat_rate: number; }
interface MCompany { seller_id: string; account_name: string; marketplaces: MMarketplace[]; }
interface MatrixData { year: number; companies: MCompany[]; weeks: MWeek[]; }

const fmtMoney = (n: number) =>
  n ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
const fmtEuro = (n: number) => (n ? `€ ${fmtMoney(n)}` : '-');
const fmtInt = (n: number) => (n ? n.toLocaleString() : '-');
const fmtDate = (s: string) => s.slice(5); // MM-DD
const GROUP_BORDER = '2px solid var(--border)';

// Colored company-group header band (mirrors the spreadsheet's per-company tint).
const COMPANY_PALETTE = [
  { bg: '#cfe2f3', fg: '#1f4e79' }, // blue   (Webaccess)
  { bg: '#fce5cd', fg: '#9c5b1a' }, // orange (Amazing Games)
  { bg: '#d9ead3', fg: '#38761d' }, // green
  { bg: '#e6d6f2', fg: '#674ea7' }, // purple
  { bg: '#f4cccc', fg: '#990000' }, // red
  { bg: '#d0e0e3', fg: '#0b5394' }, // teal
];
const VAT_BAND_BG = '#fff2a8';   // yellow "omzet" marketplace band
const VAT_BAND_FG = '#5b4b00';

const WeeklyMatrix: React.FC<{ reportType: string; sellerId: string; marketplaceId: number | null }> = ({ reportType, sellerId, marketplaceId }) => {
  const token = localStorage.getItem('access_token') ?? '';
  const [data, setData] = useState<MatrixData | null>(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [metric, setMetric] = useState<'sales' | 'units'>('sales');
  // Optional date-range filter — narrows the matrix to weeks overlapping the
  // selected range. Empty = whole year. Bounds are kept within the chosen year.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingVat, setEditingVat] = useState(false);
  const [vatDraft, setVatDraft] = useState<Record<string, string>>({});
  const [savingVat, setSavingVat] = useState(false);

  const fetchMatrix = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ year: String(year), report_type: reportType });
    if (sellerId) params.set('seller_id', sellerId);
    if (marketplaceId) params.set('marketplace_id', String(marketplaceId));
    fetch(`${API_BASE}/api/v1/sp-api/business-reports/weekly-matrix?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reportType, year, sellerId, marketplaceId, token]);

  useEffect(() => fetchMatrix(), [fetchMatrix]);

  const isSales = metric === 'sales';
  const fmt = isSales ? fmtMoney : fmtInt;       // channel cells — plain, like the sheet
  const fmtTot = isSales ? fmtEuro : fmtInt;     // aggregate totals — € prefixed
  const companies = data?.companies ?? [];

  // Flatten company → marketplace into an ordered list of leaf columns.
  const columns = React.useMemo(
    () => companies.flatMap((c, ci) =>
      c.marketplaces.map((m, idx) => ({
        key: `${c.seller_id}:${m.id}`, company: c.account_name, code: m.code,
        domain: m.domain, vat: m.vat_rate, firstInGroup: idx === 0, ci,
      }))
    ),
    [companies],
  );

  // Distinct countries (marketplaces) for the VAT editor — country-based, so we
  // dedupe by marketplace code regardless of which company it belongs to.
  const countries = React.useMemo(() => {
    const seen = new Map<string, MMarketplace>();
    companies.flatMap(c => c.marketplaces).forEach(m => { if (!seen.has(m.code)) seen.set(m.code, m); });
    return Array.from(seen.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [companies]);

  const weeks = React.useMemo(() => {
    if (!data) return [];
    return data.weeks.filter(w => {
      const hasData = isSales ? w.total || w.prev_total : w.units_total || w.prev_units_total;
      if (!hasData) return false;
      // Keep weeks whose [start, end] overlaps the selected range (ISO date
      // strings compare lexicographically, so plain string compares are safe).
      if (dateFrom && w.week_end < dateFrom) return false;
      if (dateTo && w.week_start > dateTo) return false;
      return true;
    });
  }, [data, isSales, dateFrom, dateTo]);

  const cellVal = (w: MWeek, key: string) => (isSales ? w.cells[key]?.s : w.cells[key]?.u) ?? 0;
  const wkTotal = (w: MWeek) => (isSales ? w.total : w.units_total);
  const wkPrev = (w: MWeek) => (isSales ? w.prev_total : w.prev_units_total);
  const wkDiff = (w: MWeek) => (isSales ? w.diff : w.units_diff);

  const grandTotal = weeks.reduce((s, w) => s + wkTotal(w), 0);
  const grandPrev = weeks.reduce((s, w) => s + wkPrev(w), 0);
  const colTotal = (key: string) => weeks.reduce((s, w) => s + cellVal(w, key), 0);

  const pct = (diff: number, prev: number) => (prev ? diff / prev : null);
  const yearOptions = [year + 1, year, year - 1, year - 2].filter((v, i, a) => a.indexOf(v) === i);

  const saveVat = async () => {
    setSavingVat(true);
    try {
      await Promise.all(
        Object.entries(vatDraft).map(([code, val]) => {
          const rate = (parseFloat(val) || 0) / 100;
          return fetch(`${API_BASE}/api/v1/settings/vat-rates/${code}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ vat_rate: rate }),
          });
        }),
      );
      setEditingVat(false);
      setVatDraft({});
      fetchMatrix();
    } finally {
      setSavingVat(false);
    }
  };

  const FIXED = ['Week', 'maandag', 'zondag', 'totaal', `totaal ${year - 1}`, 'Percentage'];
  const fixedAlign = (h: string): 'left' | 'right' =>
    (h === 'Week' || h === 'maandag' || h === 'zondag' ? 'left' : 'right');

  return (
    <div>
      {/* Controls */}
      <div className="card" style={{ marginBottom: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>Revenue per week — {data?.year ?? year}</div>
        <div style={{ display: 'flex', gap: 0, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {(['sales', 'units'] as const).map(m => (
            <button key={m} onClick={() => setMetric(m)}
              style={{
                padding: '4px 12px', fontSize: 12, border: 'none', cursor: 'pointer',
                background: metric === m ? 'var(--accent)' : 'transparent',
                color: metric === m ? '#fff' : 'var(--text-muted)', fontWeight: metric === m ? 600 : 500,
              }}>
              {m === 'sales' ? 'Sales (€)' : 'Shipments (units)'}
            </button>
          ))}
        </div>
        <select className="form-input" style={{ fontSize: 12, padding: '3px 8px', width: 100 }} value={year}
          onChange={e => { setYear(Number(e.target.value)); setDateFrom(''); setDateTo(''); }}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {/* Date-range filter — scoped to the selected year */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="date" className="form-input" style={{ fontSize: 12, padding: '3px 6px', width: 130 }}
            min={`${year}-01-01`} max={`${year}-12-31`} value={dateFrom}
            onChange={e => setDateFrom(e.target.value)} title="From date" />
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>→</span>
          <input type="date" className="form-input" style={{ fontSize: 12, padding: '3px 6px', width: 130 }}
            min={`${year}-01-01`} max={`${year}-12-31`} value={dateTo}
            onChange={e => setDateTo(e.target.value)} title="To date" />
          {(dateFrom || dateTo) && (
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 8px' }}
              onClick={() => { setDateFrom(''); setDateTo(''); }} title="Clear date range">✕</button>
          )}
        </div>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setEditingVat(v => !v)}>
          {editingVat ? '✕ Close VAT' : '⚙ Edit VAT %'}
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Year total</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{fmtTot(grandTotal)}</span>
        </div>
      </div>

      {/* VAT editor — country-based rates (apply to every company in that country) */}
      {editingVat && (
        <div className="card" style={{ marginBottom: 10, padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            VAT % per country — all sales figures are shown net of VAT as <code>gross ÷ (1 + VAT)</code>. Applies to all companies selling in that country.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            {countries.map(c => (
              <div key={c.code} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }} title={c.domain}>{c.code}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input className="form-input" type="number" min={0} max={100} step={0.5}
                    style={{ width: 64, fontSize: 12, padding: '3px 6px' }}
                    value={vatDraft[c.code] ?? (c.vat_rate * 100).toString()}
                    onChange={e => setVatDraft(d => ({ ...d, [c.code]: e.target.value }))} />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>%</span>
                </div>
              </div>
            ))}
            {!countries.length && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No countries with data yet.</span>}
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 14px', marginLeft: 8 }}
              onClick={saveVat} disabled={savingVat || !Object.keys(vatDraft).length}>
              {savingVat ? 'Saving…' : 'Save VAT'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner" /> Building weekly matrix…</div>
      ) : !weeks.length ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
          No data for {dateFrom || dateTo ? 'the selected date range' : year}. {dateFrom || dateTo ? 'Widen the range or clear it.' : 'Pull reports or pick another year.'}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 720 }}>
            <thead>
              {/* Group row: fixed columns + one tinted header per company spanning its marketplaces */}
              <tr>
                {FIXED.map(h => (
                  <th key={h} rowSpan={2} style={{ padding: '8px 10px', textAlign: fixedAlign(h), fontWeight: 700, fontSize: 11, color: 'var(--text)', whiteSpace: 'nowrap', background: 'var(--bg-secondary)', borderBottom: GROUP_BORDER, verticalAlign: 'bottom' }}>{h}</th>
                ))}
                {companies.map((c, ci) => {
                  const pal = COMPANY_PALETTE[ci % COMPANY_PALETTE.length];
                  return (
                    <th key={c.seller_id} colSpan={c.marketplaces.length}
                      style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12.5, color: pal.fg, background: pal.bg, whiteSpace: 'nowrap', borderLeft: GROUP_BORDER, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                      {c.account_name}
                    </th>
                  );
                })}
              </tr>
              {/* Marketplace row — yellow "omzet" band, like the sheet */}
              <tr>
                {columns.map(col => (
                  <th key={col.key} title={`${col.domain}${col.vat ? ` · VAT ${(col.vat * 100).toFixed(0)}%` : ''}`}
                    style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, color: VAT_BAND_FG, background: VAT_BAND_BG, whiteSpace: 'nowrap', borderLeft: col.firstInGroup ? GROUP_BORDER : '1px solid rgba(0,0,0,0.08)', borderBottom: GROUP_BORDER }}>
                    Amazon {col.code}{col.vat ? <span style={{ fontSize: 9, opacity: 0.75 }}> {(col.vat * 100).toFixed(0)}%</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map(w => {
                const total = wkTotal(w);
                const prev = wkPrev(w);
                const diff = wkDiff(w);
                const p = pct(diff, prev);
                return (
                  <tr key={w.week} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 10px', fontWeight: 600 }}>{w.week}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{fmtDate(w.week_start)}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{fmtDate(w.week_end)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700 }}>{fmtTot(total)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text-muted)' }}>{fmtTot(prev)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: p == null ? 'var(--text-muted)' : p >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {p == null ? '-' : `${(p * 100).toFixed(2)}%`}
                      {diff ? <span style={{ fontSize: 10, opacity: 0.7, color: 'var(--text-muted)' }}> ({diff > 0 ? '+' : ''}{fmtTot(diff)})</span> : null}
                    </td>
                    {columns.map(col => {
                      const v = cellVal(w, col.key);
                      return <td key={col.key} style={{ padding: '7px 10px', textAlign: 'right', color: v ? 'var(--text)' : 'var(--text-muted)', borderLeft: col.firstInGroup ? GROUP_BORDER : undefined }}>{fmt(v)}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {/* Totals — sales are net of VAT (excluded server-side per marketplace) */}
              <tr style={{ borderTop: GROUP_BORDER, background: 'var(--bg-secondary)', fontWeight: 700 }}>
                <td style={{ padding: '8px 10px' }} colSpan={3}>{isSales ? 'Total (net, ex-VAT)' : 'Total'}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtTot(grandTotal)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-muted)' }}>{fmtTot(grandPrev)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: !grandPrev ? 'var(--text-muted)' : grandTotal >= grandPrev ? 'var(--success)' : 'var(--danger)' }}>
                  {grandPrev ? `${(((grandTotal - grandPrev) / grandPrev) * 100).toFixed(2)}%` : '-'}
                </td>
                {columns.map(col => (
                  <td key={col.key} style={{ padding: '8px 10px', textAlign: 'right', borderLeft: col.firstInGroup ? GROUP_BORDER : undefined }}>
                    {fmt(colTotal(col.key))}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

const SpBusinessReports: React.FC<Props> = ({ account, marketplaceId }) => {
  const token = localStorage.getItem('access_token') ?? '';
  const [view, setView] = useState<'matrix' | 'detail'>('matrix');
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('GET_SALES_AND_TRAFFIC_REPORT');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [search, setSearch] = useState('');
  const [pulling, setPulling] = useState(false);
  const [jobProgress, setJobProgress] = useState<{ status: string; records_fetched: number; error?: string } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const PAGE_SIZE = 100;
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE), report_type: reportType });
    if (marketplaceId) params.set('marketplace_id', String(marketplaceId));
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (search) params.set('search', search);
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/business-reports?${params}`, { headers });
      const data = await res.json();
      setRows(data.data ?? []); setTotal(data.total ?? 0); setPage(p);
    } catch {
      // network error — keep existing data
    } finally { setLoading(false); }
  }, [account.seller_id, marketplaceId, reportType, dateFrom, dateTo, search, token]);

  // Auto-load the default window (last 30 days) on mount and whenever the
  // selected account/marketplace changes.
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.seller_id, marketplaceId]);

  const startPull = async () => {
    setPulling(true); setJobProgress(null);
    try {
      const mkIds = marketplaceId ? [marketplaceId] : account.marketplaces.map(m => m.id);
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/pull/business-reports`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marketplace_ids: mkIds, report_type: reportType,
          date_start: new Date(dateFrom).toISOString(),
          date_end: new Date(dateTo + 'T23:59:59').toISOString(),
        }),
      });
      const data = await res.json();
      if (data.job_id) {
        setJobProgress({ status: 'running', records_fetched: 0 });
        pollRef.current = setInterval(async () => {
          try {
            const r = await fetch(`${API_BASE}/api/v1/sp-api/jobs/${data.job_id}`, { headers });
            const d = await r.json();
            setJobProgress({ status: d.status, records_fetched: d.records_fetched, error: d.error });
            if (d.status !== 'running') { clearInterval(pollRef.current!); setPulling(false); load(1); }
          } catch { /* ignore poll errors */ }
        }, 2000);
      } else { setPulling(false); }
    } catch {
      setJobProgress({ status: 'failed', records_fetched: 0, error: 'Could not reach backend' });
      setPulling(false);
    }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Aggregate daily for charts (group by date, sum metrics)
  const chartData = React.useMemo(() => {
    const byDate: Record<string, { date: string; sessions: number; page_views: number; units_ordered: number; revenue: number }> = {};
    rows.forEach(r => {
      const d = r.report_date;
      if (!byDate[d]) byDate[d] = { date: d, sessions: 0, page_views: 0, units_ordered: 0, revenue: 0 };
      byDate[d].sessions += r.sessions ?? 0;
      byDate[d].page_views += r.page_views ?? 0;
      byDate[d].units_ordered += r.units_ordered ?? 0;
      byDate[d].revenue += r.ordered_product_sales ?? 0;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string | null) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = (a as any)[sortKey] ?? '';
      const bv = (b as any)[sortKey] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const sortIcon = (key: string | null) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';

  const totalRevenue = rows.reduce((s, r) => s + (r.ordered_product_sales ?? 0), 0);
  const totalUnits = rows.reduce((s, r) => s + (r.units_ordered ?? 0), 0);
  const totalSessions = rows.reduce((s, r) => s + (r.sessions ?? 0), 0);
  const avgBuyBox = rows.length ? (rows.reduce((s, r) => s + (r.buy_box_percentage ?? 0), 0) / rows.length * 100).toFixed(1) : '—';

  return (
    <div>
      {/* Sub-view toggle: Weekly Matrix (spreadsheet-style) vs per-ASIN Detail */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 0, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', width: 'fit-content' }}>
          {([
            { id: 'matrix', label: 'Weekly Matrix' },
            { id: 'detail', label: 'Detail' },
          ] as const).map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              style={{
                padding: '5px 16px', fontSize: 13, border: 'none', cursor: 'pointer',
                background: view === v.id ? 'var(--accent)' : 'transparent',
                color: view === v.id ? '#fff' : 'var(--text-muted)', fontWeight: view === v.id ? 600 : 500,
              }}>
              {v.label}
            </button>
          ))}
        </div>
        <button className="btn btn-secondary" style={{ padding: '5px 14px', fontSize: 12, marginLeft: 'auto' }}
          onClick={() => downloadExport(`/api/v1/sp-api/export/business-reports.csv?report_type=${reportType}`, 'business_reports.csv')}>
          ⬇ Export All
        </button>
      </div>

      {view === 'matrix' ? (
        <WeeklyMatrix reportType={reportType} sellerId={account.seller_id} marketplaceId={marketplaceId} />
      ) : (
      <>
      {/* Stats + Pull — single combined card */}
      <div className="card" style={{ marginBottom: 10, padding: '8px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Stat chips */}
          {[
            { label: 'Revenue', value: totalRevenue.toFixed(2), accent: 'var(--accent)' },
            { label: 'Units', value: totalUnits, accent: 'var(--success)' },
            { label: 'Sessions', value: totalSessions.toLocaleString(), accent: 'var(--info)' },
            { label: 'Avg Buy Box', value: `${avgBuyBox}%`, accent: 'var(--text-muted)' },
          ].map(({ label, value, accent }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 5, paddingRight: 12, borderRight: '1px solid var(--border)' }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
            </div>
          ))}
          {/* Pull controls inline */}
          <select className="form-input" style={{ fontSize: 11, padding: '3px 6px', width: 140 }} value={reportType} onChange={e => setReportType(e.target.value)}>
            {REPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input type="date" className="form-input" style={{ fontSize: 11, padding: '3px 6px', width: 128 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>→</span>
          <input type="date" className="form-input" style={{ fontSize: 11, padding: '3px 6px', width: 128 }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => load(1)} disabled={loading}>View</button>
          <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={startPull} disabled={pulling}>
            {pulling ? (jobProgress?.status === 'running' ? `Processing… (${jobProgress.records_fetched})` : 'Starting…') : '↓ Pull Report'}
          </button>
        </div>
        {jobProgress && (
          <div style={{ marginTop: 6, fontSize: 12, color: jobProgress.status === 'completed' ? 'var(--success)' : jobProgress.status === 'failed' ? 'var(--danger)' : 'var(--text-muted)' }}>
            {jobProgress.status === 'running' ? '⏳ Amazon is processing the report. This can take 5–10 minutes…'
              : jobProgress.status === 'completed' ? `✓ Report ready — ${jobProgress.records_fetched} rows imported`
              : `✗ ${jobProgress.error}`}
          </div>
        )}
      </div>

      {/* Charts */}
      {chartData.length > 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Sessions & Page Views</span></div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="sessions" stroke="var(--accent)" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="page_views" stroke="var(--info)" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Units Ordered & Revenue</span></div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="units_ordered" fill="var(--success)" opacity={0.8} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="var(--warning)" dot={false} strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Search / filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
        <input className="form-input" style={{ flex: '1 1 220px', fontSize: 12, padding: '4px 8px' }} placeholder="Search ASIN or SKU…"
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load(1)} />
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => load(1)} disabled={loading}>Apply</button>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => { setSearch(''); setTimeout(() => load(1), 0); }}>Reset</button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner" /> Loading report data…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                {([
                  { h: 'Date', key: 'report_date' },
                  { h: 'ASIN', key: 'asin' },
                  { h: 'SKU', key: 'sku' },
                  { h: 'Sessions', key: 'sessions' },
                  { h: 'Page Views', key: 'page_views' },
                  { h: 'Buy Box %', key: 'buy_box_percentage' },
                  { h: 'Units', key: 'units_ordered' },
                  { h: 'Revenue', key: 'ordered_product_sales' },
                  { h: 'Order Items', key: 'total_order_items' },
                  { h: 'Country', key: 'marketplace_id' },
                ] as { h: string; key: string | null }[]).map(({ h, key }) => (
                  <th key={h} onClick={() => handleSort(key)}
                    style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: sortKey === key ? 'var(--accent)' : 'var(--text-muted)', cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    {h}{key ? <span style={{ opacity: sortKey === key ? 1 : 0.35, fontSize: 10 }}>{sortIcon(key)}</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => {
                const mkCode = account.marketplaces.find(m => m.id === r.marketplace_id)?.code ?? '—';
                return (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}>{r.report_date}</td>
                  <td style={{ padding: '9px 12px' }}><code style={{ fontSize: 12 }}>{r.asin ?? '—'}</code></td>
                  <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}><code style={{ fontSize: 12 }}>{r.sku ?? '—'}</code></td>
                  <td style={{ padding: '9px 12px' }}>{r.sessions?.toLocaleString() ?? '—'}</td>
                  <td style={{ padding: '9px 12px' }}>{r.page_views?.toLocaleString() ?? '—'}</td>
                  <td style={{ padding: '9px 12px' }}>
                    {r.buy_box_percentage != null ? (
                      <span style={{ color: r.buy_box_percentage > 0.8 ? 'var(--success)' : r.buy_box_percentage > 0.5 ? 'var(--warning)' : 'var(--danger)' }}>
                        {(r.buy_box_percentage * 100).toFixed(1)}%
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '9px 12px', fontWeight: 600 }}>{r.units_ordered ?? '—'}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: 'var(--success)' }}>
                    {r.ordered_product_sales != null ? r.ordered_product_sales.toFixed(2) : '—'}
                  </td>
                  <td style={{ padding: '9px 12px' }}>{r.total_order_items ?? '—'}</td>
                  <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>{mkCode}</td>
                </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No report data. Pull a report to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 13 }}>Page {page} of {totalPages}</span>
          <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default SpBusinessReports;
